import { useState } from "react"
import { reportingSourceSchema } from "@aix/aixinternal-shared/reporting"
import type { ReportingProduct } from "@aix/aixinternal-shared/reporting"
import { Button, Input, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@aix/ui"

import { trpc } from "../../lib/trpc"
import { formatReportingValidationIssues } from "./reportingValidation"

export function SeoInventorySetup({
  companyId,
  products,
  onSaved,
}: {
  companyId: string
  products: ReportingProduct[]
  onSaved: () => void
}) {
  const [productKey, setProductKey] = useState("")
  const [key, setKey] = useState("")
  const [externalId, setExternalId] = useState("")
  const [tools, setTools] = useState("")
  const [confirmed, setConfirmed] = useState(false)
  const [message, setMessage] = useState("")
  const mutation = trpc.reporting.configureSource.useMutation({
    onSuccess: () => {
      setMessage("AIX SEO ownership saved. No pages were published or collected.")
      setConfirmed(false)
      onSaved()
    },
    onError: (error) => setMessage(error.message),
  })
  return (
    <form
      className="space-y-3 border-t border-border pt-4"
      onSubmit={(event) => {
        event.preventDefault()
        if (!confirmed) return
        const parsed = reportingSourceSchema.safeParse({
          key,
          productKey,
          provider: "aix-seo",
          channel: "organic-search",
          externalId,
          environment: "production",
          timezone: "America/Los_Angeles",
          currency: null,
          attributionWindow: null,
          toolPaths: tools
            .split(",")
            .map((path) => path.trim())
            .filter(Boolean),
        })
        if (!parsed.success) {
          setMessage(formatReportingValidationIssues(parsed.error.issues))
          return
        }
        mutation.mutate({ companyId, source: parsed.data })
      }}
    >
      <h3 className="font-medium">AIX SEO catalog ownership</h3>
      <p className="text-xs text-muted-foreground">
        Use the exact existing SEO product UUID, not its display name. Saving grants this company reporting access to
        its catalog; its domain must match a configured host. Tools require exact paths; no /tools prefix guessing.
        Bindings are immutable.
      </p>
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="space-y-1 text-sm">
          <span>Reporting product</span>
          <Select
            value={productKey || undefined}
            onValueChange={(value) => {
              setProductKey(value)
              setConfirmed(false)
            }}
          >
            <SelectTrigger aria-label="SEO inventory product">
              <SelectValue placeholder="Choose product" />
            </SelectTrigger>
            <SelectContent>
              {products.map((product) => (
                <SelectItem key={product.key} value={product.key}>
                  {product.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
        <label className="space-y-1 text-sm">
          <span>Stable inventory source key</span>
          <Input
            value={key}
            onChange={(event) => {
              setKey(event.target.value)
              setConfirmed(false)
            }}
            required
          />
        </label>
        <label className="space-y-1 text-sm">
          <span>Verified AIX SEO product UUID</span>
          <Input
            value={externalId}
            onChange={(event) => {
              setExternalId(event.target.value)
              setConfirmed(false)
            }}
            required
          />
        </label>
        <label className="space-y-1 text-sm">
          <span>Exact tool paths, comma separated (optional)</span>
          <Input
            value={tools}
            onChange={(event) => {
              setTools(event.target.value)
              setConfirmed(false)
            }}
            placeholder="/tools/editor,/calculator"
          />
        </label>
      </div>
      <label className="flex items-start gap-2 text-sm">
        <input type="checkbox" checked={confirmed} onChange={(event) => setConfirmed(event.target.checked)} />
        <span>
          I verified this SEO product belongs to this company and reporting product, including its domain and listed
          tool paths.
        </span>
      </label>
      <Button type="submit" loading={mutation.isLoading} disabled={!confirmed || !productKey}>
        Save SEO inventory binding
      </Button>
      {message ? (
        <p role="status" className="text-sm">
          {message}
        </p>
      ) : null}
    </form>
  )
}
